import { yupResolver } from "@hookform/resolvers/yup";
import defaultAxios, { AxiosError } from "axios";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import Swal from "sweetalert2";
import { captchaSiteKey } from "../../../constants/common";
import {
    ErrorFieldResponse,
    RegistrasiForm,
    RegistrasiResponse,
} from "../../../constants/types";
import axios from "../../../services/axios";
import useKota from "../../../services/queries/useKota";
import useProvinsi from "../../../services/queries/useProvinsi";
import { getRegisterDefaultValues, registerSchema } from "../registerSchema";

const useRegisterForm = () => {
    const history = useHistory();
    const [showPassword, setShowPassword] = useState(false);
    const [showPasswordConfirmation, setShowPasswordConfirmation] =
        useState(false);
    const [loading, setLoading] = useState(false);
    const [captchaToken, setCaptchaToken] = useState<string | null>(null);
    const [agreedToTerms, setAgreedToTerms] = useState(false);
    const [provinceId, setProvinceId] = useState<number>();

    const { data: provinsiOptions, isLoading: isLoadingProvinsi } =
        useProvinsi();
    const { data: kotaOptions, isLoading: isLoadingKota } = useKota(provinceId);

    const referral = useMemo(() => {
        const params = new URLSearchParams(window.location.search);
        return params.get("ref") ?? "";
    }, []);

    const form = useForm<RegistrasiForm>({
        resolver: yupResolver(registerSchema),
        defaultValues: getRegisterDefaultValues(referral),
    });

    useEffect(() => {
        form.reset(getRegisterDefaultValues(referral));
    }, [form, referral]);

    const handleErrorResponse = (error: unknown) => {
        if (!defaultAxios.isAxiosError(error)) return;

        const serverError = error as AxiosError<ErrorFieldResponse | undefined>;
        if (!serverError.response) return;

        const fieldError = serverError.response.data;

        Swal.fire({
            title: "Terjadi Kesalahan!",
            text: "Data yang diberikan salah",
            icon: "error",
            confirmButtonColor: "#45A779",
            customClass: {
                container: "my-swal",
            },
        });

        if (!fieldError?.errors) return;

        Object.keys(fieldError.errors).forEach((key) => {
            const errorMessage = fieldError.errors[key];
            form.setError(key as keyof RegistrasiForm, {
                type: "manual",
                message: errorMessage[0],
            });
        });
    };

    const onSubmit = async (values: RegistrasiForm) => {
        if (!captchaToken) {
            Swal.fire({
                title: "Terjadi Kesalahan!",
                text: "Tolong centang box reCAPTCHA",
                icon: "error",
                confirmButtonColor: "#45A779",
                customClass: {
                    container: "my-swal",
                },
            });
            return;
        }

        setLoading(true);

        try {
            const formattedNoTelp = `62${values.no_telp}`;
            const formData = new FormData();

            formData.append("nama", values.nama);
            formData.append("username", values.username);
            formData.append("email", values.email);
            formData.append("no_telp", formattedNoTelp);
            formData.append("alamat", values.alamat);
            formData.append("password", values.password);
            formData.append("role", "10");
            formData.append("referral", values.referral);
            formData.append("namaToko", values.namaToko);
            formData.append("no_telpToko", formattedNoTelp);
            formData.append("alamatToko", values.alamat);
            formData.append("warnaToko", "#45A779");

            if (values.provinsi?.id) {
                formData.append("provinsi", String(values.provinsi.id));
            }

            if (values.kota?.id) {
                formData.append("kota", String(values.kota.id));
            }

            const { data } = await axios.post<RegistrasiResponse>(
                "/api/register",
                formData,
                {
                    headers: {
                        "Content-Type": "multipart/form-data",
                    },
                },
            );

            if (data.code === 200) {
                history.push("/verifikasi");
            }
        } catch (error) {
            handleErrorResponse(error);
            console.error(error);
        } finally {
            setLoading(false);
        }
    };

    return {
        agreedToTerms,
        captchaSiteKey,
        form,
        isLoadingKota,
        isLoadingProvinsi,
        kotaOptions: kotaOptions?.content ?? [],
        loading,
        onCaptchaChange: (value: string | null) => {
            setCaptchaToken(value);
        },
        onCaptchaExpired: () => {
            setCaptchaToken(null);
        },
        onProvinceChange: (nextProvinceId?: number) => {
            setProvinceId(nextProvinceId);
            form.setValue("kota", undefined);
        },
        onSubmit,
        provinsiOptions: provinsiOptions?.content ?? [],
        setAgreedToTerms,
        showPassword,
        showPasswordConfirmation,
        togglePasswordConfirmationVisibility: () => {
            setShowPasswordConfirmation((previousState) => !previousState);
        },
        togglePasswordVisibility: () => {
            setShowPassword((previousState) => !previousState);
        },
    };
};

export type RegisterFormModel = ReturnType<typeof useRegisterForm>;

export default useRegisterForm;
